import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { DocumentTextIcon, NewspaperIcon } from '@heroicons/react/24/outline';
import { ConfirmDialog } from '@/components/shared';
import { SiPortalEmBreve } from '../../components/SiPortalEmBreve';
import { SI_PORTAL_BASE_PATH } from '../../siPortal.definitions';
import {
  MAXIMO_FIXADAS,
  STATUS_PUBLICACAO_BADGE,
  STATUS_PUBLICACAO_LABELS,
  categoriaDe,
  estaFixada,
  dataHoraAgendamento,
  millis,
} from '../publicacao.definitions';
import type { Publicacao, StatusPublicacao, TipoPublicacao } from '../publicacao.types';
import { usePodePublicar, usePublicacaoGestaoMutations, usePublicacoesGestaoQuery } from '../usePublicacoes';
import { DocumentoEditorDrawer } from '../../documentos/gestao/DocumentoEditorDrawer';
import { NovaVersaoDrawer } from '../../documentos/gestao/NovaVersaoDrawer';
import { HistoricoVersoesDrawer } from '../../documentos/gestao/HistoricoVersoesDrawer';
import { PublicacaoEditorDrawer } from '../editor/PublicacaoEditorDrawer';
import { ProgressoCiencia } from '../components/ProgressoCiencia';
import { AcaoBloqueada } from '../components/AcaoBloqueada';

const FILTROS: Array<{ valor: StatusPublicacao | ''; label: string }> = [
  { valor: '', label: 'Todas' },
  { valor: 'publicada', label: 'Publicadas' },
  { valor: 'agendada', label: 'Agendadas' },
  { valor: 'rascunho', label: 'Rascunhos' },
  { valor: 'despublicada', label: 'Despublicadas' },
];

const data = (ms: number | null) => (ms ? new Date(ms).toLocaleDateString('pt-BR') : '');

function linhaDeDetalhe(p: Publicacao): string {
  const partes = [categoriaDe(p).label];
  if (p.status === 'publicada' && p.tipo === 'documento') {
    partes.push(`atualizado em ${data(millis(p.anunciadoEm) ?? millis(p.publicarEm))}`);
    if (p.anunciarNoInicio) partes.push('anunciado na Início');
  } else if (p.status === 'publicada') partes.push(`publicada em ${data(millis(p.publicarEm))}`);
  else if (p.status === 'agendada') {
    const quando = millis(p.publicarEm);
    if (!quando) partes.push('sem data de publicação');
    else if (quando <= Date.now()) partes.push('publicando em instantes');
    else partes.push(`agendada para ${dataHoraAgendamento(new Date(quando))}`);
  } else partes.push(`editada em ${data(millis(p.atualizadoEm))}`);
  if (p.ciencia?.exige) partes.push(p.ciencia.prazo ? `ciência até ${data(millis(p.ciencia.prazo))}` : 'ciência sem prazo');
  else partes.push('sem ciência');
  if (p.fixada?.ativa) partes.push(p.fixada.ate ? `fixada até ${data(millis(p.fixada.ate))}` : 'fixada');
  return partes.join(' · ');
}

const ACAO = 'text-xs font-semibold text-primary-700 hover:text-primary-800';
const ACAO_PERIGO = 'text-xs font-semibold text-raro-gray-500 hover:text-rose-600';

/** Confirmação na tela (não o confirm do navegador). Sem `acao` = só aviso ("Entendi"). */
interface Confirmacao {
  titulo: string;
  mensagem: string;
  tipo: 'danger' | 'warning' | 'info';
  textoConfirmar: string;
  acao?: () => void;
}

/**
 * Aba Publicações: Posts (role "Publicar no Portal") e Documentos (também Administrar SI). O
 * Administrar SI vê os posts em modo leitura (lista, leituras, relatório de ciência): os botões
 * aparecem com cadeado e a dica de quem pode.
 */
export default function PublicacoesGestaoPage() {
  const { data: carregadas = [], isLoading } = usePublicacoesGestaoQuery();
  const acoes = usePublicacaoGestaoMutations();
  const podePublicar = usePodePublicar();
  // Quem só gere documentos (Administrar SI) começa em Documentos.
  const [modo, setModo] = useState<TipoPublicacao>(podePublicar ? 'post' : 'documento');
  const somenteLeitura = modo === 'post' && !podePublicar;
  /** Ação normal ou, em modo leitura, o mesmo rótulo com cadeado e dica. */
  const acao = (rotulo: string, onClick: () => void, perigo = false) =>
    somenteLeitura ? (
      <AcaoBloqueada className="text-xs font-semibold">{rotulo}</AcaoBloqueada>
    ) : (
      <button type="button" className={perigo ? ACAO_PERIGO : ACAO} onClick={onClick}>
        {rotulo}
      </button>
    );
  const todas = carregadas.filter((p) => (p.tipo ?? 'post') === modo);
  const [filtro, setFiltro] = useState<StatusPublicacao | ''>('');
  const [editor, setEditor] = useState<{ aberto: boolean; publicacao: Publicacao | null }>({ aberto: false, publicacao: null });
  const [novaVersao, setNovaVersao] = useState<Publicacao | null>(null);
  const [historico, setHistorico] = useState<Publicacao | null>(null);
  const agora = useMemo(() => Date.now(), []);
  const [confirmacao, setConfirmacao] = useState<Confirmacao | null>(null);

  const exibidas = todas.filter((p) => !filtro || p.status === filtro);
  const fixadasAgora = carregadas.filter((p) => p.status === 'publicada' && estaFixada(p, agora)).length;

  const fixar = (p: Publicacao) => {
    if (fixadasAgora >= MAXIMO_FIXADAS) {
      setConfirmacao({
        titulo: 'Limite de fixadas',
        mensagem: `Já existem ${MAXIMO_FIXADAS} publicações fixadas no topo da Início. Desafixe uma antes de fixar outra.`,
        tipo: 'info',
        textoConfirmar: 'Entendi',
      });
      return;
    }
    acoes.fixar.mutate({ id: p.id, ate: null });
  };

  return (
    <div className="space-y-4">
      {/* O que gerenciar — abas com sublinhado, numa linha própria, pra não confundir com as
          pílulas do portal nem com os filtros de status logo abaixo. */}
      <div role="tablist" aria-label="O que gerenciar" className="flex gap-6 border-b border-raro-gray-200" data-tour="si-pub-tipos">
        {(
          [
            { valor: 'post', label: 'Posts', descricao: 'informativos do feed', Icone: NewspaperIcon },
            { valor: 'documento', label: 'Documentos', descricao: 'políticas, manuais, termos', Icone: DocumentTextIcon },
          ] as const
        ).map((m) => (
          <button
            key={m.valor}
            type="button"
            role="tab"
            aria-selected={modo === m.valor}
            onClick={() => {
              setModo(m.valor);
              setFiltro('');
            }}
            className={clsx(
              '-mb-px flex items-center gap-2 border-b-2 px-1 pb-3 pt-1 font-montserrat text-sm transition-colors',
              modo === m.valor
                ? 'border-primary-600 font-semibold text-primary-700'
                : 'border-transparent font-medium text-raro-gray-500 hover:text-primary-700'
            )}
          >
            <m.Icone className="h-5 w-5" aria-hidden="true" />
            {m.label}
            <span className="hidden text-xs font-normal text-raro-gray-400 sm:inline">· {m.descricao}</span>
            {m.valor === 'post' && !podePublicar && (
              <span className="rounded-full bg-raro-gray-100 px-2 py-0.5 text-[10px] font-semibold text-raro-gray-500">
                Somente leitura
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-1 text-xs" data-tour="si-pub-status">
          <span className="mr-1 font-medium text-raro-gray-500">Status:</span>
          {FILTROS.map((f) => (
            <button
              key={f.valor || 'todas'}
              type="button"
              onClick={() => setFiltro(f.valor)}
              className={clsx(
                'rounded-full px-3 py-1.5 font-montserrat transition-colors',
                filtro === f.valor
                  ? 'bg-primary-50 font-semibold text-primary-700 ring-1 ring-primary-200'
                  : 'font-medium text-raro-gray-600 hover:bg-white hover:text-primary-700'
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div data-tour="si-pub-nova">
        {somenteLeitura ? (
          <AcaoBloqueada className="btn-primary pr-6 opacity-60">+ Nova publicação</AcaoBloqueada>
        ) : (
          <button type="button" className="btn-primary" onClick={() => setEditor({ aberto: true, publicacao: null })}>
            {modo === 'documento' ? '+ Novo documento' : '+ Nova publicação'}
          </button>
        )}
        </div>
      </div>

      {isLoading ? (
        <div className="h-40 animate-pulse rounded-2xl bg-raro-gray-100" />
      ) : exibidas.length === 0 ? (
        <SiPortalEmBreve
          icon={NewspaperIcon}
          titulo={todas.length === 0 ? (modo === 'documento' ? 'Nenhum documento ainda' : 'Nenhuma publicação ainda') : 'Nada neste filtro'}
          descricao={
            todas.length === 0
              ? `Use "${modo === 'documento' ? '+ Novo documento' : '+ Nova publicação'}" para criar o primeiro.`
              : 'Escolha outro filtro acima.'
          }
        />
      ) : (
        <ul className="divide-y divide-raro-gray-100 overflow-hidden rounded-2xl border border-raro-gray-200 bg-white shadow-sm">
          {exibidas.map((p) => {
            const fixada = estaFixada(p, agora);
            return (
              <li key={p.id} className="grid gap-3 px-5 py-4 md:grid-cols-[1fr_auto_auto] md:items-center md:gap-6">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-raro-gray-900">
                    {fixada && <span title="Fixada no topo da Início">📌 </span>}
                    {p.titulo}
                    {p.tipo === 'documento' && (
                      <span className="ml-1.5 rounded-full bg-raro-gray-100 px-2 py-0.5 text-xs font-semibold text-raro-gray-600">
                        v{p.versao ?? 1}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-raro-gray-500">{linhaDeDetalhe(p)}</p>
                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1" data-tour="si-pub-acoes">
                    {acao('Editar', () => setEditor({ aberto: true, publicacao: p }))}
                    {p.tipo === 'documento' ? (
                      <>
                        {p.arquivo && (
                          <a href={p.arquivo.url} target="_blank" rel="noreferrer" className={ACAO}>
                            Abrir arquivo
                          </a>
                        )}
                        {p.status === 'publicada' && (
                          <button type="button" className={ACAO} onClick={() => setNovaVersao(p)}>
                            ⬆ Nova versão
                          </button>
                        )}
                        {(p.versao ?? 1) > 1 && (
                          <button type="button" className={ACAO} onClick={() => setHistorico(p)}>
                            Histórico de versões
                          </button>
                        )}
                      </>
                    ) : (
                      <Link to={`${SI_PORTAL_BASE_PATH}/inicio/publicacao/${p.id}`} className={ACAO}>
                        {p.status === 'publicada' ? 'Abrir' : 'Pré-visualizar'}
                      </Link>
                    )}
                    {p.status === 'publicada' && p.tipo !== 'documento' &&
                      (p.fixada?.ativa ? acao('Desafixar', () => acoes.desafixar.mutate(p.id)) : acao('📌 Fixar no topo', () => fixar(p)))}
                    {p.status === 'publicada' &&
                      p.ciencia?.exige &&
                      acao('Pedir nova ciência', () =>
                          setConfirmacao({
                            titulo: 'Pedir nova ciência',
                            mensagem: `Quem já confirmou "${p.titulo}" vai precisar confirmar de novo, e a publicação volta a aparecer como pendente para todos os cobrados.`,
                            tipo: 'warning',
                            textoConfirmar: 'Pedir nova ciência',
                            acao: () => acoes.pedirNovaCiencia.mutate(p.id),
                          })
                      )}
                    {p.status === 'publicada' &&
                      acao(
                        'Despublicar',
                        () =>
                          setConfirmacao({
                            titulo: 'Despublicar',
                            mensagem: `"${p.titulo}" some da Início para todos. Ciências, leituras e comentários ficam guardados, e dá para publicar de novo depois.`,
                            tipo: 'warning',
                            textoConfirmar: 'Despublicar',
                            acao: () => acoes.despublicar.mutate(p.id),
                          }),
                        true
                      )}
                    {p.status === 'despublicada' && acao('Publicar de novo', () => acoes.publicar.mutate({ id: p.id, quando: null }))}
                    {acao(
                      'Excluir',
                      () =>
                        setConfirmacao({
                          titulo: p.status === 'rascunho' ? 'Excluir rascunho' : 'Excluir publicação',
                          mensagem:
                            p.status === 'rascunho'
                              ? `O rascunho "${p.titulo}" e os arquivos dele serão apagados.`
                              : `"${p.titulo}" será apagada de vez, junto com ciências, leituras, curtidas, comentários e arquivos. Não dá para desfazer. Para só tirar do ar mantendo o histórico, use "Despublicar".`,
                          tipo: 'danger',
                          textoConfirmar: 'Excluir',
                          acao: () => acoes.excluir.mutate(p.id),
                        }),
                      true
                    )}
                  </div>
                </div>
                <div>
                  {p.status === 'publicada' && p.ciencia?.exige ? (
                    <div data-tour="si-pub-ciencia">
                      <ProgressoCiencia publicacao={p} />
                    </div>
                  ) : p.status === 'publicada' ? (
                    <p className="text-sm text-raro-gray-600 md:text-right">
                      {p.leiturasTotal ?? 0} leram · ♥ {p.curtidasTotal ?? 0} · 💬 {p.comentariosTotal ?? 0}
                    </p>
                  ) : (
                    <p className="text-sm text-raro-gray-400 md:text-right">—</p>
                  )}
                </div>
                <span className={`w-fit rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_PUBLICACAO_BADGE[p.status]}`}>
                  {STATUS_PUBLICACAO_LABELS[p.status]}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        isOpen={confirmacao != null}
        title={confirmacao?.titulo ?? ''}
        message={confirmacao?.mensagem ?? ''}
        type={confirmacao?.tipo}
        confirmText={confirmacao?.textoConfirmar}
        cancelText={confirmacao?.acao ? 'Cancelar' : 'Fechar'}
        onCancel={() => setConfirmacao(null)}
        onConfirm={() => {
          confirmacao?.acao?.();
          setConfirmacao(null);
        }}
      />
      <DocumentoEditorDrawer
        isOpen={editor.aberto && modo === 'documento'}
        documento={editor.publicacao}
        onClose={() => setEditor({ aberto: false, publicacao: null })}
      />
      <NovaVersaoDrawer documento={novaVersao} onClose={() => setNovaVersao(null)} />
      <HistoricoVersoesDrawer documento={historico} onClose={() => setHistorico(null)} />
      <PublicacaoEditorDrawer
        isOpen={editor.aberto && modo === 'post'}
        publicacao={editor.publicacao}
        todas={carregadas}
        onClose={() => setEditor({ aberto: false, publicacao: null })}
      />
    </div>
  );
}
